import { useEffect, useRef, useState } from "react";
import { Newspaper, Plus, Edit, Trash2, Eye, EyeOff, ImagePlus, Loader2, ExternalLink, Save, X, Image as ImageIcon, Copy, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { api } from "./adminApi";
import { formatDateFr } from "@/lib/formatters";

const EMPTY = { title: "", excerpt: "", body: "", cover_image: "", tags: [], status: "draft", slug: "" };

export default function NewsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // { article_id?, form }
  const [uploading, setUploading] = useState(false);
  const [uploadingInline, setUploadingInline] = useState(false);
  const [inlineImages, setInlineImages] = useState([]); // {url, filename, caption}
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiRefining, setAiRefining] = useState(false);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);
  const inlineInputRef = useRef(null);

  const fetchList = async () => {
    try {
      setLoading(true);
      const r = await api.get("/admin/news");
      setList(r.data || []);
    } catch { toast.error("Impossible de charger les actualités"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchList(); }, []);

  const openEditor = async (article) => {
    setInlineImages([]);
    setAiPrompt("");
    if (article?.article_id) {
      // Load the full article (body excluded from list)
      try {
        const r = await api.get(`/admin/news/${article.article_id}`);
        setEditor({ article_id: r.data.article_id, form: { ...EMPTY, ...r.data, tags: r.data.tags || [] } });
      } catch { toast.error("Impossible de charger l'article"); }
    } else {
      setEditor({ form: { ...EMPTY } });
    }
  };

  const closeEditor = () => setEditor(null);

  const setField = (k, v) => setEditor((e) => ({ ...e, form: { ...e.form, [k]: v } }));

  const handleCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/news/upload-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("cover_image", r.data.url);
      toast.success("Image téléversée");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
    finally { setUploading(false); }
  };

  const insertAtCursor = (snippet) => {
    const ta = bodyRef.current;
    const current = editor?.form?.body || "";
    if (!ta) {
      setField("body", `${current}\n${snippet}\n`);
      return;
    }
    const start = ta.selectionStart ?? current.length;
    const end = ta.selectionEnd ?? current.length;
    const next = current.slice(0, start) + snippet + current.slice(end);
    setField("body", next);
    // Replace cursor right after the inserted snippet on next tick.
    setTimeout(() => {
      ta.focus();
      const pos = start + snippet.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleInlineUpload = async (file) => {
    if (!file) return;
    setUploadingInline(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/news/upload-inline", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url;
      const altRaw = (file.name || "Illustration").replace(/\.[a-z0-9]+$/i, "").replace(/[<>"']/g, "");
      setInlineImages((prev) => [...prev, { url, filename: file.name, caption: altRaw }]);
      // Auto-insertion uniquement si pas de prompt IA en cours (sinon l'IA placera l'image).
      if (!aiPrompt.trim()) {
        const snippet = `\n<figure style="margin:24px 0;text-align:center;">\n  <img src="${url}" alt="${altRaw}" style="max-width:100%;height:auto;" />\n  <figcaption style="font-size:12px;color:#777;margin-top:6px;">${altRaw}</figcaption>\n</figure>\n`;
        insertAtCursor(snippet);
      }
      toast.success(aiPrompt.trim() ? "Image ajoutée à la bibliothèque (l'IA la placera)" : "Image insérée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setUploadingInline(false);
      if (inlineInputRef.current) inlineInputRef.current.value = "";
    }
  };

  const updateInlineCaption = (idx, caption) => {
    setInlineImages((prev) => prev.map((img, i) => (i === idx ? { ...img, caption } : img)));
  };
  const removeInlineImage = (idx) => {
    setInlineImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const refineWithAi = async (directive) => {
    const currentBody = (editor?.form?.body || "").trim();
    if (currentBody.length < 30) {
      toast.error("Rédigez ou générez d'abord un article avant de l'améliorer.");
      return;
    }
    let customInstruction = "";
    if (directive === "custom") {
      customInstruction = window.prompt("Précisez l'instruction (ex: « ton plus simple », « rajouter une section FAQ »)") || "";
      if (!customInstruction.trim()) return;
    }
    setAiRefining(true);
    try {
      const r = await api.post("/admin/news/refine", {
        title: editor.form.title || "",
        excerpt: editor.form.excerpt || "",
        tags: editor.form.tags || [],
        body: currentBody,
        directive: directive === "custom" ? "polish" : directive,
        custom_instruction: customInstruction,
      });
      const d = r.data || {};
      if (d.title) setField("title", d.title);
      if (d.excerpt) setField("excerpt", d.excerpt);
      if (Array.isArray(d.tags) && d.tags.length) setField("tags", d.tags);
      if (d.body) setField("body", d.body);
      toast.success("Article retravaillé — relisez avant publication");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur amélioration IA");
    } finally {
      setAiRefining(false);
    }
  };

  const generateWithAi = async () => {
    const prompt = aiPrompt.trim();
    if (prompt.length < 10) {
      toast.error("Décrivez un peu plus le contexte (10 caractères min)");
      return;
    }
    const currentBody = (editor?.form?.body || "").trim();
    if (currentBody && !window.confirm("Le contenu actuel sera remplacé par celui généré par l'IA. Continuer ?")) {
      return;
    }
    setAiGenerating(true);
    try {
      const r = await api.post("/admin/news/generate", { prompt, images: inlineImages });
      const d = r.data || {};
      if (d.title) setField("title", d.title);
      if (d.excerpt) setField("excerpt", d.excerpt);
      if (Array.isArray(d.tags) && d.tags.length) setField("tags", d.tags);
      if (d.body) setField("body", d.body);
      toast.success("Article généré par l'IA — relisez et ajustez avant publication");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur génération IA");
    } finally {
      setAiGenerating(false);
    }
  };

  const copyImageUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copiée dans le presse-papier");
    } catch { /* noop */ }
  };

  const save = async (publish) => {
    const f = editor.form;
    if (!f.title.trim()) { toast.error("Le titre est requis"); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      if (publish) payload.status = "published";
      let r;
      if (editor.article_id) {
        r = await api.patch(`/admin/news/${editor.article_id}`, payload);
      } else {
        r = await api.post("/admin/news", payload);
        // If creating and publishing in one go, push the status patch
        if (publish && r.data?.article_id) {
          r = await api.patch(`/admin/news/${r.data.article_id}`, { status: "published" });
        }
      }
      toast.success(publish ? "Article publié" : "Article sauvegardé");
      closeEditor();
      fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const togglePublish = async (a) => {
    const newStatus = a.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/admin/news/${a.article_id}`, { status: newStatus });
      toast.success(newStatus === "published" ? "Article publié" : "Article repassé en brouillon");
      fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement "${a.title}" ?`)) return;
    try {
      await api.delete(`/admin/news/${a.article_id}`);
      toast.success("Article supprimé");
      fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="news-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-[#3F3F3F] flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-[#662483]" /> Actualités
          </h3>
          <p className="text-xs text-gray-500">Gérez les articles publiés sur la page Actualités du site.</p>
        </div>
        <Button onClick={() => openEditor(null)} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none" data-testid="news-create-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouvel article
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 py-6 text-center">Chargement…</p>
      ) : list.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 p-10 text-center text-gray-500">
          <Newspaper className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>Aucun article. Créez le premier !</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((a) => (
            <div key={a.article_id} className="bg-white border border-gray-200 p-3 flex items-center gap-3" data-testid={`news-row-${a.article_id}`}>
              {a.cover_image ? (
                <img
                  src={a.cover_image.startsWith("http") ? a.cover_image : `${process.env.REACT_APP_BACKEND_URL}${a.cover_image}`}
                  alt=""
                  className="w-20 h-14 object-cover bg-gray-100 shrink-0"
                />
              ) : (
                <div className="w-20 h-14 bg-gradient-to-br from-[#662483] to-[#0069B4] flex items-center justify-center shrink-0">
                  <Newspaper className="w-5 h-5 text-white/60" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#3F3F3F] truncate">{a.title}</p>
                <p className="text-[11px] text-gray-500 truncate">
                  {a.status === "published" ? "Publié" : "Brouillon"}
                  {(a.published_at || a.created_at) && ` · ${formatDateFr(a.published_at || a.created_at)}`}
                  {a.views > 0 && ` · ${a.views} vue(s)`}
                  {a.tags?.length ? ` · ${a.tags.join(", ")}` : ""}
                </p>
              </div>
              <Badge className={`rounded-none text-[10px] ${a.status === "published" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                {a.status === "published" ? "✓ En ligne" : "Brouillon"}
              </Badge>
              <div className="flex items-center gap-1 shrink-0">
                {a.status === "published" && (
                  <a href={`/actualites/${a.slug}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-gray-100" title="Voir sur le site">
                    <ExternalLink className="w-4 h-4 text-[#0069B4]" />
                  </a>
                )}
                <button onClick={() => togglePublish(a)} className="p-1.5 hover:bg-gray-100" title={a.status === "published" ? "Dépublier" : "Publier"}>
                  {a.status === "published" ? <EyeOff className="w-4 h-4 text-orange-500" /> : <Eye className="w-4 h-4 text-green-600" />}
                </button>
                <button onClick={() => openEditor(a)} className="p-1.5 hover:bg-gray-100" title="Éditer" data-testid={`news-edit-${a.article_id}`}>
                  <Edit className="w-4 h-4 text-[#662483]" />
                </button>
                <button onClick={() => remove(a)} className="p-1.5 hover:bg-gray-100" title="Supprimer">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      <Dialog open={!!editor} onOpenChange={(v) => !v && closeEditor()}>
        <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="news-editor">
          <DialogHeader className="bg-gradient-to-r from-[#662483] to-[#0069B4] p-4 shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <Newspaper className="w-5 h-5" />
              {editor?.article_id ? "Modifier l'article" : "Nouvel article"}
            </DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="overflow-y-auto p-6 space-y-4 flex-1">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Titre *</Label>
                <Input value={editor.form.title} onChange={(e) => setField("title", e.target.value)} className="rounded-none" placeholder="Ex. Forum emploi logistique à Montpellier" data-testid="news-title" />
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Chapô / Extrait court</Label>
                <Textarea value={editor.form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} className="rounded-none" rows={2} placeholder="1-2 lignes affichées sur la liste et en haut de l'article" />
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Tags <span className="text-gray-400 font-normal">(séparés par des virgules)</span></Label>
                <Input
                  value={(editor.form.tags || []).join(", ")}
                  onChange={(e) => setField("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
                  className="rounded-none"
                  placeholder="forum-emploi, logistique, transport"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Image de couverture</Label>
                <div className="flex items-center gap-3 flex-wrap">
                  {editor.form.cover_image ? (
                    <img
                      src={editor.form.cover_image.startsWith("http") ? editor.form.cover_image : `${process.env.REACT_APP_BACKEND_URL}${editor.form.cover_image}`}
                      alt=""
                      className="w-40 h-24 object-cover bg-gray-100 border border-gray-200"
                    />
                  ) : (
                    <div className="w-40 h-24 bg-gradient-to-br from-[#662483] to-[#0069B4] flex items-center justify-center text-white text-xs">
                      Pas d&apos;image
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => handleCover(e.target.files?.[0])} data-testid="news-cover-input" />
                    <span className="inline-flex items-center gap-1 text-xs bg-white border border-[#662483] text-[#662483] px-3 py-2 hover:bg-[#F0E9F3]">
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
                      {uploading ? "Envoi…" : (editor.form.cover_image ? "Remplacer" : "Téléverser")}
                    </span>
                  </label>
                  {editor.form.cover_image && (
                    <button onClick={() => setField("cover_image", "")} className="text-xs text-red-500 hover:underline">Retirer</button>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">JPG, PNG, WEBP — max 5 Mo. Format conseillé : 1600×900 (16/9).</p>
              </div>
              {/* AI generation panel */}
              <div className="border border-[#662483]/30 bg-gradient-to-br from-[#F8F4FD] to-[#EAF2FB] p-4 space-y-3" data-testid="news-ai-panel">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#662483]" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#662483]">Génération par IA</h4>
                </div>
                <p className="text-[11px] text-gray-600 -mt-1">
                  Décrivez le contexte (qui, quoi, quand, où) et collez votre texte brut. L&apos;IA produira un article structuré (titre + chapô + tags + HTML) respectant la charte graphique. Téléversez d&apos;abord vos images : elles seront placées automatiquement aux endroits pertinents.
                </p>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Contexte + texte brut</Label>
                  <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    rows={5}
                    placeholder={`Exemple :\nArticle sur le Forum Emploi Logistique organisé à Bourgoin-Jallieu le mardi 12 mars 2026 de 9h à 17h. 50 entreprises présentes (Carrefour, FM Logistic, GXO...). 1200 candidats attendus, 200 postes ouverts (cariste, prépa commandes, agent de tri). Stand FR INTERIM pour rencontrer directement nos consultants. Préinscription gratuite sur notre site.`}
                    className="rounded-none text-xs"
                    data-testid="news-ai-prompt"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                    <Label className="text-xs font-semibold block">Images de l&apos;article ({inlineImages.length})</Label>
                    <label className="cursor-pointer inline-flex items-center gap-1 text-[11px] bg-white border border-[#662483] text-[#662483] px-2 py-1 hover:bg-[#F0E9F3]">
                      <input ref={inlineInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" className="hidden" onChange={(e) => handleInlineUpload(e.target.files?.[0])} data-testid="news-inline-input" />
                      {uploadingInline ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                      {uploadingInline ? "Envoi…" : "Téléverser une image"}
                    </label>
                  </div>
                  {inlineImages.length === 0 ? (
                    <p className="text-[10px] text-gray-400 italic">Aucune image. Vous pouvez générer sans images si besoin.</p>
                  ) : (
                    <div className="space-y-2" data-testid="news-inline-images-bank">
                      {inlineImages.map((img, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 p-2">
                          <img src={img.url.startsWith("http") ? img.url : `${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt="" className="w-12 h-12 object-cover shrink-0" />
                          <div className="flex-1 min-w-0 space-y-1">
                            <Input value={img.caption || ""} onChange={(e) => updateInlineCaption(i, e.target.value)} placeholder="Légende suggérée à l'IA" className="rounded-none text-xs h-7" />
                            <button onClick={() => copyImageUrl(img.url)} className="text-[10px] text-[#0069B4] hover:underline inline-flex items-center gap-1 max-w-full truncate" title={img.url}>
                              <Copy className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">{img.url}</span>
                            </button>
                          </div>
                          <button onClick={() => removeInlineImage(i)} className="p-1 hover:bg-gray-100 text-red-500 shrink-0" title="Retirer">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  onClick={generateWithAi}
                  disabled={aiGenerating || !aiPrompt.trim()}
                  className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none w-full"
                  data-testid="news-ai-generate-btn"
                >
                  {aiGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {aiGenerating ? "Génération en cours…" : "Générer avec l'IA"}
                </Button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                  <Label className="text-xs font-semibold block">Contenu (HTML autorisé)</Label>
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          disabled={aiRefining || !(editor.form.body || "").trim()}
                          className="cursor-pointer inline-flex items-center gap-1 text-xs bg-white border border-[#0069B4] text-[#0069B4] px-3 py-1.5 hover:bg-[#E6F0F7] disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="news-refine-trigger"
                        >
                          {aiRefining ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                          {aiRefining ? "Amélioration…" : "Améliorer avec l'IA"}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-none w-64">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-gray-500">Choisir une direction</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => refineWithAi("polish")} data-testid="news-refine-polish">
                          <Sparkles className="w-3.5 h-3.5 mr-2 text-[#662483]" /> Améliorer le style (par défaut)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => refineWithAi("shorter")} data-testid="news-refine-shorter">
                          ✂️ Raccourcir (-30%)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => refineWithAi("longer")} data-testid="news-refine-longer">
                          📝 Étoffer (+30%)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => refineWithAi("dynamic")} data-testid="news-refine-dynamic">
                          ⚡ Ton plus dynamique
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => refineWithAi("professional")} data-testid="news-refine-pro">
                          🎓 Ton plus institutionnel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => refineWithAi("seo")} data-testid="news-refine-seo">
                          🔎 Optimiser SEO
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => refineWithAi("custom")} data-testid="news-refine-custom">
                          ✏️ Instruction personnalisée…
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <label className="cursor-pointer inline-flex items-center gap-1 text-xs bg-white border border-[#662483] text-[#662483] px-3 py-1.5 hover:bg-[#F0E9F3]" data-testid="news-insert-image-btn">
                      <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif" className="hidden" onChange={(e) => handleInlineUpload(e.target.files?.[0])} />
                      {uploadingInline ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                      {uploadingInline ? "Envoi…" : "Insérer une image"}
                    </label>
                  </div>
                </div>
                <Textarea
                  ref={bodyRef}
                  value={editor.form.body}
                  onChange={(e) => setField("body", e.target.value)}
                  className="rounded-none font-mono text-xs"
                  rows={14}
                  placeholder={`<p>Texte d'introduction…</p>\n<h2>Sous-titre</h2>\n<p>Paragraphe avec <strong>gras</strong> et <a href="https://exemple.fr">lien</a>.</p>\n<ul><li>Liste à puces</li></ul>`}
                  data-testid="news-body"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Vous pouvez aussi rédiger manuellement le HTML. Balises autorisées : <code>&lt;p&gt;</code>, <code>&lt;h2&gt;</code>, <code>&lt;strong&gt;</code>, <code>&lt;a&gt;</code>, <code>&lt;ul&gt;</code>, <code>&lt;img&gt;</code>, <code>&lt;figure&gt;</code>, <code>&lt;blockquote&gt;</code>…
                </p>
              </div>
              {editor.article_id && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold mb-1 block">Slug (URL)</Label>
                    <Input value={editor.form.slug || ""} onChange={(e) => setField("slug", e.target.value)} className="rounded-none font-mono text-xs" />
                    <p className="text-[10px] text-gray-500 mt-1">URL : /actualites/<strong>{editor.form.slug}</strong></p>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold mb-1 block">Statut</Label>
                    <p className="text-xs h-9 flex items-center">
                      <Badge className={`rounded-none ${editor.form.status === "published" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>
                        {editor.form.status === "published" ? "En ligne" : "Brouillon"}
                      </Badge>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="border-t border-gray-200 p-4 flex justify-end gap-2 bg-[#FAFAFA] shrink-0">
            <Button variant="outline" onClick={closeEditor} className="rounded-none">
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button variant="outline" onClick={() => save(false)} disabled={saving} className="rounded-none" data-testid="news-save-draft">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Sauvegarder en brouillon
            </Button>
            <Button onClick={() => save(true)} disabled={saving} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none" data-testid="news-publish">
              <Eye className="w-4 h-4 mr-1" /> Publier
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
